import { useEffect, useRef, useState } from 'react';
import { smartAssistantSettingsApi, type WaConnectionState, type WhatsAppStatus } from '../api/smartAssistantSettingsApi';

const STATE_META: Record<WaConnectionState, { dot: string; label: string; sub?: string }> = {
  disconnected: { dot: 'bg-rose-500', label: 'غير متصل' },
  connecting: { dot: 'bg-amber-400', label: 'جارٍ الاتصال…' },
  qr_ready: { dot: 'bg-amber-400', label: 'امسح رمز QR' },
  connected: { dot: 'bg-emerald-500', label: 'متصل' },
  reconnecting: { dot: 'bg-amber-400', label: 'إعادة الاتصال…', sub: 'جارٍ استعادة جلستك.' },
  error: { dot: 'bg-rose-500', label: 'خطأ في الاتصال' },
};

export function WhatsAppConnectionCard({
  canConnect,
  canDisconnect,
  onStatusChange,
}: {
  canConnect: boolean;
  canDisconnect: boolean;
  onStatusChange?: (s: WhatsAppStatus | null) => void;
}) {
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState<WaConnectionState>('disconnected');
  const [status, setStatus] = useState<WhatsAppStatus | null>(null);
  const [qr, setQr] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const [testTo, setTestTo] = useState('');
  const [testMsg, setTestMsg] = useState('');
  const pollRef = useRef<number | null>(null);

  const stopPolling = () => {
    if (pollRef.current !== null) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const refresh = async () => {
    const r = await smartAssistantSettingsApi.getWhatsAppStatus();
    setLoading(false);
    if (!r.ok) { setError('تعذر تحميل حالة الاتصال. حاول مرة أخرى.'); return; }
    setError('');
    setStatus(r.data);
    setState(r.data.session.status);
    onStatusChange?.(r.data);
    if (r.data.session.status === 'connected') {
      setQr(null);
      stopPolling(); // connected → no more QR polling
    } else if (r.data.session.status === 'qr_ready' || r.data.session.status === 'connecting') {
      // Session is already waiting for a scan (e.g. after a reconnect) —
      // start fetching/polling the QR immediately, no extra click needed.
      if (pollRef.current === null) pollQr();
    }
  };

  const pollQr = () => {
    stopPolling();
    pollRef.current = window.setInterval(async () => {
      const s = await smartAssistantSettingsApi.getWhatsAppStatus();
      if (s.ok) {
        setStatus(s.data);
        setState(s.data.session.status);
        onStatusChange?.(s.data);
        if (s.data.session.status === 'connected') {
          setQr(null);
          stopPolling();
          return;
        }
      }
      const q = await smartAssistantSettingsApi.getWhatsAppQr();
      // never blank out a good QR with a transient null (QR rotates)
      if (q.ok && q.data.qr) setQr(q.data.qr);
    }, 3000);
    // fetch immediately too — don't wait for the first interval tick
    void smartAssistantSettingsApi.getWhatsAppQr().then((q) => {
      if (q.ok && q.data.qr) setQr(q.data.qr);
    });
  };

  useEffect(() => {
    void refresh();
    return stopPolling;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connect = async () => {
    setBusy(true); setError('');
    const r = await smartAssistantSettingsApi.connectWhatsApp();
    setBusy(false);
    if (!r.ok) { setError('تعذر الاتصال بواتساب. حاول مرة أخرى.'); return; }
    setState(r.data.status === 'connected' ? 'connected' : 'qr_ready');
    pollQr();
  };

  const reconnect = async () => {
    setBusy(true); setError(''); setState('reconnecting');
    const r = await smartAssistantSettingsApi.reconnectWhatsApp();
    setBusy(false);
    if (!r.ok) { setError('تعذرت إعادة الاتصال. حاول مرة أخرى.'); setState('error'); return; }
    pollQr();
  };

  const disconnect = async () => {
    setBusy(true); setError('');
    const r = await smartAssistantSettingsApi.disconnectWhatsApp();
    setBusy(false);
    setConfirmDisconnect(false);
    if (!r.ok) { setError('تعذر قطع الاتصال. حاول مرة أخرى.'); return; }
    setQr(null); stopPolling(); setState('disconnected');
    void refresh();
  };

  const sendTest = async () => {
    if (!testTo.trim()) return;
    setBusy(true); setTestMsg('');
    const r = await smartAssistantSettingsApi.testWhatsApp(testTo.trim());
    setBusy(false);
    setTestMsg(r.ok ? 'تم إرسال الرسالة التجريبية ✅' : 'تعذر إرسال الرسالة التجريبية. حاول مرة أخرى.');
  };

  const meta = STATE_META[state];

  return (
    <div className="rounded-2xl bg-white border border-gray-100 p-6 max-w-2xl">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-sm font-black text-gray-900 flex items-center gap-2">
          <i className="ri-whatsapp-line text-emerald-600 text-lg" /> WhatsApp
        </h3>
        <span className={`w-2 h-2 rounded-full ${meta.dot}`} />
      </div>
      <p className="text-xs text-gray-500 mb-5">
        اربط حساب واتساب الخاص بـ DressnMore ليتمكن المساعد الذكي من التواصل مع العملاء.
      </p>

      {loading ? (
        <div className="animate-pulse h-24 rounded-xl bg-gray-50" />
      ) : (
        <>
          {error && (
            <div className="mb-4 rounded-xl border border-rose-100 bg-rose-50 px-4 py-2.5 text-xs font-semibold text-rose-700 flex items-center justify-between">
              {error}
              <button type="button" onClick={() => { setError(''); void refresh(); }} className="font-bold underline">حاول مجددًا</button>
            </div>
          )}

          <div className="flex items-center gap-2 mb-4">
            <span className={`w-2.5 h-2.5 rounded-full ${meta.dot} ${state === 'connecting' || state === 'reconnecting' ? 'animate-pulse' : ''}`} />
            <span className="text-sm font-bold text-gray-800">{meta.label}</span>
            {meta.sub && <span className="text-xs text-gray-400">{meta.sub}</span>}
          </div>

          {state === 'connected' && status ? (
            <div className="space-y-4">
              <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-xs">
                <div><dt className="text-gray-400">رقم واتساب</dt><dd className="font-bold text-gray-800">{status.session.phone ?? '—'}</dd></div>
                <div><dt className="text-gray-400">الحساب</dt><dd className="font-bold text-gray-800">{status.session.display_name ?? '—'}</dd></div>
                <div><dt className="text-gray-400">تاريخ الاتصال</dt><dd className="font-bold text-gray-800">{status.session.connected_at ? new Date(status.session.connected_at).toLocaleString() : '—'}</dd></div>
                <div><dt className="text-gray-400">آخر ظهور</dt><dd className="font-bold text-gray-800">{status.session.last_seen_at ? new Date(status.session.last_seen_at).toLocaleString() : '—'}</dd></div>
                <div><dt className="text-gray-400">الجلسة</dt><dd className="font-bold text-emerald-700">نشطة</dd></div>
                <div><dt className="text-gray-400">البوابة</dt><dd className={`font-bold ${status.gateway.healthy ? 'text-emerald-700' : 'text-rose-600'}`}>{status.gateway.healthy ? 'تعمل' : 'غير متاحة'}</dd></div>
              </dl>

              <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-gray-50">
                <input
                  value={testTo}
                  onChange={(e) => setTestTo(e.target.value)}
                  placeholder="رقم تجريبي +20…"
                  className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs w-44"
                />
                <button type="button" disabled={busy} onClick={() => void sendTest()}
                  className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-bold text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                  إرسال رسالة تجريبية
                </button>
                {canDisconnect && (
                  <button type="button" onClick={() => setConfirmDisconnect(true)}
                    className="px-3 py-1.5 rounded-lg bg-rose-50 border border-rose-100 text-xs font-bold text-rose-700 hover:bg-rose-100">
                    قطع الاتصال
                  </button>
                )}
              </div>
              {testMsg && <p className="text-xs text-gray-500">{testMsg}</p>}
            </div>
          ) : null}

          {state === 'qr_ready' ? (
            <div className="space-y-3">
              {qr ? (
                <img src={qr} alt="رمز QR لواتساب" className="w-52 h-52 rounded-xl border border-gray-200" />
              ) : (
                <div className="w-52 h-52 rounded-xl border border-gray-100 bg-gray-50 animate-pulse flex items-center justify-center text-xs text-gray-400">بانتظار رمز QR…</div>
              )}
              <p className="text-xs text-gray-500 leading-relaxed">
                امسح رمز QR هذا بتطبيق واتساب<br />
                افتح واتساب ← الأجهزة المرتبطة ← ربط جهاز ← امسح الرمز
              </p>
            </div>
          ) : null}

          {(state === 'disconnected' || state === 'error') && canConnect ? (
            <button type="button" disabled={busy} onClick={() => void connect()}
              className="px-4 py-2 rounded-xl bg-emerald-600 text-white text-xs font-bold hover:bg-emerald-700 disabled:opacity-50">
              {busy ? 'جارٍ الاتصال…' : 'ربط واتساب'}
            </button>
          ) : null}

          {state === 'disconnected' && !canConnect ? (
            <p className="text-xs text-gray-400">ليس لديك صلاحية ربط واتساب.</p>
          ) : null}

          {state === 'reconnecting' ? (
            <p className="text-xs text-amber-600 font-semibold">جارٍ استعادة جلستك…</p>
          ) : null}

          {confirmDisconnect && (
            <div className="fixed inset-0 z-40 bg-black/30 flex items-center justify-center p-4">
              <div className="rounded-2xl bg-white p-6 max-w-sm w-full shadow-xl">
                <h4 className="text-sm font-black text-gray-900">قطع اتصال واتساب؟</h4>
                <p className="text-xs text-gray-500 mt-2 leading-relaxed">
                  لن يتلقى العملاء ردودًا تلقائية من المساعد عبر هذا الرقم بعد الآن.
                </p>
                <div className="mt-4 flex justify-end gap-2">
                  <button type="button" onClick={() => setConfirmDisconnect(false)}
                    className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-bold text-gray-700">إلغاء</button>
                  <button type="button" disabled={busy} onClick={() => void disconnect()}
                    className="px-3 py-1.5 rounded-lg bg-rose-600 text-white text-xs font-bold disabled:opacity-50">قطع الاتصال</button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
